/**
 * The web side of the expo-owned sign-in handoff
 * (docs/plans/expo-owned-signin.md), Phases 2 and 3.
 *
 * Native owns refresh; the webview never refreshes. This module implements
 * the web side of the handshake: ask native for the current session, apply
 * whatever native pushes via `setSession`, and forward any token the
 * *webview* itself mints (MFA step-up) back to native so it can keep owning
 * refresh. Phase 3 adds the sign-out coordination in both directions
 * (`signOutToNative`, plus the `auth-session: null` handling in
 * `applyNativeSession`) and the pull backstops in
 * `installNativeSessionBackstops`.
 *
 * Everything here only runs when `expoSupportsAuth()` is true — older native
 * builds that predate this handoff never see these outbound messages (see
 * `postToNative`'s own guard in `nativeBridge.ts`), and never send the
 * inbound `auth-session` event either, so `listenForNativeSessionPushes`
 * simply never fires for them.
 */

import { Session, SupabaseClient } from '@supabase/supabase-js'
import {
  AUTH_SESSION_EVENT,
  AuthSessionEvent,
  AuthSessionEventDetail,
  expoSupportsAuth,
  postToNative,
} from './nativeBridge'

// Matches expoPushImplementation.ts's sendAndWait timeout. Native should
// answer well within this under normal conditions (SecureStore read + at
// most a token refresh); if it doesn't, native has crashed, is stuck, or
// predates this handoff in a way `expoSupportsAuth()` failed to catch.
const HANDSHAKE_TIMEOUT_MS = 10000

// Settles the currently in-flight `requestAuthSession()` call, if any, when
// an `auth-session` event arrives — set by `requestAuthSession`, consumed by
// the single persistent listener in `listenForNativeSessionPushes`. Kept
// module-level (not per-listener-instance) because there is only ever one
// logical "webview waiting on a reply" at a time — see `requestAuthSession`'s
// in-flight-promise reuse below.
interface PendingHandshake {
  resolve: (detail: AuthSessionEventDetail | null) => void
  reject: (error: Error) => void
}
let pendingHandshake: PendingHandshake | null = null

// Tracks the last access token *native* injected. Serves two purposes:
//   1. The echo guard in `forwardTokenChangesToNative` — "native just gave us
//      this token" (don't forward, native already knows) vs. "the webview
//      minted a new token itself" (do forward, e.g. after MFA step-up).
//   2. Whether the webview currently holds a native-owned session at all, so
//      `applyNativeSession` can tell a native-initiated sign-out (`null` after
//      we held a session) apart from the ordinary boot handshake against a
//      signed-out native (`null` when we never had one).
let lastNativeAccessToken: string | null = null

/**
 * Apply what native pushed to the given client.
 *
 * A token pair is applied via `setSession` and recorded as native-injected so
 * the echo guard below doesn't bounce it straight back.
 *
 * `detail: null` means native has no session. If it had previously given us
 * one, that's a **native-initiated sign-out** (native menu, or native's own
 * refresh failing) and the webview must clear its copy too — that's what drops
 * the `/admin` `auth_token` cookie, via `supabase.tsx`'s `onAuthStateChange`.
 * `scope: 'local'` because native has already handled the server side; a
 * global revoke from here would hit the network with a refresh token that is
 * very likely already invalid. If we never held a native session (the ordinary
 * boot handshake against a signed-out native, including the existing-install
 * migration window) this is a no-op.
 *
 * Note the ordering: `lastNativeAccessToken` is updated **before** each
 * awaited call, then rolled back if that call fails. Updating it first is
 * load-bearing, not incidental — supabase-js notifies `onAuthStateChange`
 * subscribers *during* `setSession`, before it resolves, so
 * `forwardTokenChangesToNative` runs while we're still awaiting. If the token
 * weren't recorded by then it would look webview-minted and get bounced right
 * back to native, which is the echo loop the guard exists to prevent. Clearing
 * first in the `null` branch likewise de-dupes two sign-out pushes arriving
 * back to back. Rolling back on failure keeps the tracker honest without
 * giving up either property.
 */
async function applyNativeSession(
  client: SupabaseClient,
  detail: AuthSessionEventDetail | null
): Promise<void> {
  const previous = lastNativeAccessToken

  if (!detail) {
    if (previous === null) return
    lastNativeAccessToken = null
    const { error } = await client.auth.signOut({ scope: 'local' })
    if (error) {
      // The client still holds the session we failed to clear, so restore the
      // tracker — otherwise a follow-up sign-out push would be read as "we
      // never had a native session" and silently skip the retry.
      lastNativeAccessToken = previous
      throw error
    }
    return
  }

  lastNativeAccessToken = detail.access_token
  const { error } = await client.auth.setSession({
    access_token: detail.access_token,
    refresh_token: detail.refresh_token,
  })
  if (error) {
    // The client rejected the pair (typically already-revoked), so it never
    // became our session — don't leave the tracker pointing at a token the
    // client never adopted, or a later `null` push would be misread as a
    // native-initiated sign-out of a session that was never established.
    lastNativeAccessToken = previous
    throw error
  }
}

/**
 * Register the single, app-lifetime listener for native-pushed sessions.
 * Call once, early — e.g. from `SupabaseProvider`'s mount effect, before any
 * `requestAuthSession()` call can fire — and keep it registered for as long
 * as the app runs. Every `auth-session` CustomEvent native dispatches flows
 * through here uniformly, whichever of these three things it actually is:
 *
 * 1. The reply to an in-flight `requestAuthSession()` handshake (resolves
 *    that promise via `pendingResolve` in addition to applying the session).
 * 2. An unsolicited re-injection after native's own `TOKEN_REFRESHED`.
 * 3. An unsolicited push after native adopts the legacy session handed to it
 *    during the existing-install migration (see `awaitNativeSession`).
 * 4. A `null` push when native signs out on its own (native menu, or its own
 *    refresh failing) — see `applyNativeSession`.
 *
 * A pending handshake is settled only **after** the session has actually been
 * applied, and is rejected if applying it fails (e.g. native handed over an
 * already-revoked pair). That's what lets `AwaitNativeSession` show its retry
 * state instead of proceeding on a session the client never accepted — the gap
 * Phase 2 left open. Failures on an unsolicited push have no handshake to
 * reject, so they're logged.
 *
 * Returns an unsubscribe function.
 */
export function listenForNativeSessionPushes(client: SupabaseClient): () => void {
  const onAuthSession = (event: Event) => {
    const detail = (event as AuthSessionEvent).detail
    const handshake = pendingHandshake
    pendingHandshake = null
    applyNativeSession(client, detail).then(
      () => handshake?.resolve(detail),
      (err: unknown) => {
        const error = err instanceof Error ? err : new Error(String(err))
        if (handshake) {
          handshake.reject(error)
        } else {
          console.error('[nativeAuth] Failed to apply native-pushed session', error)
        }
      }
    )
  }
  window.addEventListener(AUTH_SESSION_EVENT, onAuthSession as EventListener)
  return () => window.removeEventListener(AUTH_SESSION_EVENT, onAuthSession as EventListener)
}

// A second `requestAuthSession()` call while the first is still in flight
// (e.g. the user tapping "retry" on the AwaitNativeSession error state)
// reuses the same promise instead of registering a second concurrent
// resolver — mirrors the `resolved` guard in expoPushImplementation.ts's
// sendAndWait.
let inFlightRequest: Promise<AuthSessionEventDetail | null> | null = null

/**
 * Ask native for the current Supabase session and wait for its reply.
 * Resolves `null` when native explicitly has no session (signed out, or —
 * during the existing-install migration window — genuinely never signed
 * in). Rejects on timeout, or if applying the session native replied with
 * fails.
 *
 * `listenForNativeSessionPushes` must already be registered before this is
 * called, or the reply has nothing to resolve against.
 *
 * Callers must NOT treat a rejection as "no session" and fall through to
 * web sign-in — `startupFlow.tsx` hard-gates `SignInForm` behind
 * `!window.expoSupportsAuth` specifically so a native build can never reach
 * it, and silently falling through here would defeat that gate. Show a
 * distinct retry state instead.
 */
export function requestAuthSession(): Promise<AuthSessionEventDetail | null> {
  if (inFlightRequest) {
    return inFlightRequest
  }

  const promise = new Promise<AuthSessionEventDetail | null>((resolve, reject) => {
    let settled = false
    let timer: ReturnType<typeof setTimeout>

    pendingHandshake = {
      resolve: (detail) => {
        if (settled) return
        settled = true
        clearTimeout(timer)
        resolve(detail)
      },
      reject: (error) => {
        if (settled) return
        settled = true
        clearTimeout(timer)
        reject(error)
      },
    }

    timer = setTimeout(() => {
      if (settled) return
      settled = true
      pendingHandshake = null
      reject(new Error('Timed out waiting for native to answer request-auth-session'))
    }, HANDSHAKE_TIMEOUT_MS)

    postToNative('request-auth-session')
  }).finally(() => {
    inFlightRequest = null
  })

  inFlightRequest = promise
  return promise
}

/**
 * Wire this to the client's `onAuthStateChange`. Forwards a newly-minted
 * token pair to native (`auth-session-updated`) whenever the webview itself
 * rotated the session — the MFA correctness trap the plan calls out:
 * `mfa.challengeAndVerify` mints a new aal2 pair that native doesn't know
 * about, and if left unforwarded native's refresh token goes stale under it
 * (symptom: users signed out ~1 hour after doing MFA).
 *
 * Skips the forward when the access token matches the last one *native*
 * injected — otherwise applying a native-pushed session would immediately
 * echo the same token back to native as if the webview had minted it.
 */
export function forwardTokenChangesToNative(session: Session | null): void {
  if (!session) return
  if (session.access_token === lastNativeAccessToken) return
  postToNative('auth-session-updated', {
    access_token: session.access_token,
    refresh_token: session.refresh_token,
  })
}

/**
 * Tell native the webview signed the user out, so it clears its own session
 * and returns to `NativeSignIn` instead of sitting on a webview whose session
 * is gone (Phase 3 of the plan). No-ops in a browser, and for native builds
 * that predate the handoff, via `postToNative`'s own gate.
 *
 * Note the webview's `signOut()` is a **global** revoke, which also kills the
 * refresh token native holds — so native must be told, and its handler uses
 * `scope: 'local'` since the server side is already done.
 *
 * Also drops our record of the native-injected token: without this, a
 * subsequent `auth-session: null` push from native would be read as a
 * *native*-initiated sign-out and trigger a second, redundant local sign-out.
 */
export function signOutToNative(): void {
  lastNativeAccessToken = null
  postToNative('sign-out')
}

/**
 * One-time raw read of a pre-Phase-2, webview-owned session for the
 * existing-install migration (see the plan's "Existing-install migration"
 * section). Must be called BEFORE `createClient` — once the client exists
 * with `persistSession: false` (set whenever `expoSupportsAuth()`), its own
 * init path never touches localStorage at all, so this reads the raw key
 * directly instead. `localStorage.getItem` is synchronous, so no async
 * plumbing is needed to fit this ahead of `createClient` in module-eval
 * order.
 *
 * The storage key is supabase-js's own default:
 * `sb-<hostname's first label>-auth-token` (see `SupabaseClient.js`'s
 * `defaultStorageKey`, derived from `new
 * URL(authUrl).hostname.split('.')[0]`) — computed from the same
 * `supabaseUrl` the client itself is constructed with, so the two can't
 * drift.
 *
 * **This consumes the key** (hence `take`, not `read`), which matters for
 * sign-out: with `persistSession: false` nothing else ever writes or clears
 * that entry, so a legacy pair left in place would be re-offered to native on
 * *every* subsequent webview load where native has no session — including the
 * load right after a sign-out. Native's own migration check is already
 * one-shot (`hasAttemptedLegacyMigration`), so consuming here just makes the
 * two sides agree. The in-memory value survives for the lifetime of the page,
 * so `AwaitNativeSession`'s retry button still has it.
 */
export function takeLegacyAuthTokenRaw(supabaseUrl: string): AuthSessionEventDetail | null {
  try {
    const projectRef = new URL(supabaseUrl).hostname.split('.')[0]
    const storageKey = `sb-${projectRef}-auth-token`
    const raw = localStorage.getItem(storageKey)
    if (!raw) return null
    localStorage.removeItem(storageKey)
    const parsed = JSON.parse(raw)
    if (typeof parsed?.access_token !== 'string' || typeof parsed?.refresh_token !== 'string') {
      return null
    }
    return { access_token: parsed.access_token, refresh_token: parsed.refresh_token }
  } catch {
    return null
  }
}

export type AwaitNativeSessionResult =
  | { status: 'authenticated' }
  | { status: 'signed-out' }

/**
 * The full Phase-2 handoff, run once at startup by `AwaitNativeSession`:
 * ask native for a session. If native has one, `listenForNativeSessionPushes`
 * has already applied it to the client by the time this resolves (both are
 * driven by the same `auth-session` event). If native truly has none, hand
 * it our pre-Phase-2 legacy session (if this browser still has one) via
 * `auth-session-updated` so native can adopt it — the same "webview holds a
 * token pair, native should adopt it" message used for the MFA back-push,
 * extended here to also carry an explicit `null` when there is no legacy
 * session to hand over. That explicit-null signal lets native distinguish
 * "the webview checked and found nothing" (migration attempt complete, safe
 * to show its own sign-in screen) from "the webview hasn't replied yet".
 *
 * If native does end up adopting the handed-over legacy session, it pushes
 * a fresh `auth-session` event back down (the same push path used for
 * `TOKEN_REFRESHED`), which `listenForNativeSessionPushes` picks up
 * independently of this function's return value — this call only reports
 * the *initial* handshake outcome for the caller's UI state machine.
 */
export async function awaitNativeSession(
  legacySession: AuthSessionEventDetail | null
): Promise<AwaitNativeSessionResult> {
  const detail = await requestAuthSession()
  if (detail) {
    return { status: 'authenticated' }
  }
  postToNative('auth-session-updated', legacySession)
  return { status: 'signed-out' }
}

// Bounds how often the pull backstops below can ask native for a session.
// Native's push is the primary path; these are only there to close the window
// where a push landed late (RN throttles timers while backgrounded) or not at
// all. Without a cooldown, a page issuing a burst of requests against a
// genuinely dead session would post one `request-auth-session` per 401.
const PULL_COOLDOWN_MS = 30000
let lastPullAt = 0

/**
 * Ask native for its current session outside of the startup handshake. The
 * reply flows through `listenForNativeSessionPushes` like any other
 * `auth-session` event, so the client is updated as a side effect; nothing
 * here needs the result. A rejection means native didn't answer in time —
 * logged, not thrown, since this is a backstop and the caller (an event
 * handler) has nothing useful to do with the failure.
 */
function pullNativeSession(reason: string): void {
  const now = Date.now()
  if (now - lastPullAt < PULL_COOLDOWN_MS) return
  lastPullAt = now
  requestAuthSession().catch((err) => {
    console.warn(`[nativeAuth] session pull (${reason}) failed`, err)
  })
}

/**
 * Install the **pull** half of the token-freshness design (Phase 3 of the
 * plan). Native pushing on its own `TOKEN_REFRESHED` is the primary path, but
 * React Native throttles timers while the app is backgrounded, so a push can
 * land late — or after the webview has already tried to use a token that
 * expired in the meantime. These two backstops cover that window:
 *
 * - **`visibilitychange` → visible:** the app just came back to the
 *   foreground; ask for whatever native has now rather than waiting for its
 *   throttled refresh timer to catch up.
 * - **Any `401` response:** something we sent was rejected as unauthorized,
 *   which for an RLS or `/backend/...` Bearer call means the in-memory access
 *   token is stale. `window.fetch` is wrapped rather than each call site
 *   patched, because "any RLS/backend call" includes everything supabase-js
 *   issues internally, and there is no shared wrapper those go through.
 *
 * Both are strictly additive — they only ever ask native for a session, never
 * refresh anything themselves, which is what keeps native the sole owner of
 * refresh.
 *
 * No-ops (and installs nothing, notably no `fetch` wrapper) unless
 * `expoSupportsAuth()`, so browsers and pre-handoff native builds are
 * untouched. Returns an uninstall function.
 */
export function installNativeSessionBackstops(): () => void {
  if (!expoSupportsAuth()) return () => {}

  const onVisibilityChange = () => {
    if (document.visibilityState === 'visible') {
      pullNativeSession('visibilitychange')
    }
  }
  document.addEventListener('visibilitychange', onVisibilityChange)

  const originalFetch = window.fetch
  // `fetch` must keep its `window` receiver — calling a detached reference
  // throws "Illegal invocation".
  const boundFetch = originalFetch.bind(window)
  const patchedFetch: typeof window.fetch = async (...args) => {
    const response = await boundFetch(...args)
    if (response.status === 401) {
      pullNativeSession('401')
    }
    return response
  }
  window.fetch = patchedFetch

  return () => {
    document.removeEventListener('visibilitychange', onVisibilityChange)
    // Only restore if nothing else wrapped fetch after us — clobbering a later
    // wrapper would be worse than leaving ours in place.
    if (window.fetch === patchedFetch) {
      window.fetch = originalFetch
    }
  }
}
